import type { Email } from "@@/shared/email";
import { emailFactory, emailZodSchema } from "@@/shared/email";
import type { EntityState } from "@@/shared/entity-status";
import { entityStateZodSchema } from "@@/shared/entity-status";
import type { ImageReference } from "@@/shared/images/image-reference";
import type { NewsletterId } from "@@/shared/newsletters";
import type { Phone, PhoneNumber } from "@@/shared/phone";
import { phoneFactory, phoneZodSchema } from "@@/shared/phone";
import type { LanguageCode } from "@@/shared/translations/language-code";
import type { ZodSchema } from "zod";
import { z } from "zod";
import type { IsoAndUnixTimestamp } from "../date-time/iso-and-unix";
import { isoAndUnixZodSchema, nowIsoAndUnix } from "../date-time/iso-and-unix";
import type { Customer } from "./customer";
import type { UserForProvider } from "./user-for-provider";
import type { UserId } from "./user-id";
import { userIdFactory, userIdZodSchema } from "./user-id";
import type { UserReference } from "./user-reference";
import { createUserReference } from "./user-reference";

type User = {
    _id: UserId;
    _type: "USER";
    _status: EntityState;
    _createdAt: IsoAndUnixTimestamp;
    image?: ImageReference;

    firstName: string;
    lastName: string;

    scopes: string[];
    email: Email;
    phone: Phone;

    preferences?: {
        newsletters?: NewsletterId[];
        /** only used for debugging so far | 2024 */
        language?: LanguageCode;
    };

    anonymized?: {
        at: IsoAndUnixTimestamp;
        by: UserId;
        requestedBy: "USER" | "SYSTEM"; //User requested it or the system automaticly did it archiving
    };
};

type FactoryParams = {
    _id?: UserId;
    firstName: string;
    lastName: string;
    emailAddress: string;
    emailAddressVerified: boolean;
    phoneNumber: PhoneNumber;
    phoneNumberVerified: boolean;
    preferences: {
        newsletters?: NewsletterId[];
        language?: LanguageCode;
    };
    scopes: string[];
};
const userFactory = (params: FactoryParams): User => {
    return userZodSchema.parse({
        _id: params._id ?? userIdFactory(),
        _type: "USER",
        _status: "ACTIVE",
        _createdAt: nowIsoAndUnix(),
        firstName: params.firstName,
        lastName: params.lastName,
        email: emailFactory({
            emailAddress: params.emailAddress,
            verified: params.emailAddressVerified,
        }), //  we know either email or phone has been provided since we check it above
        phone: phoneFactory({
            phoneNumber: params.phoneNumber,
            verified: params.phoneNumberVerified,
        }), // we know either email or phone has been provided since we check it above
        scopes: [...params.scopes],
        preferences: params.preferences,
    });
};

const userZodSchema: ZodSchema<User> = z.object({
    _id: userIdZodSchema,
    _type: z.literal("USER"),
    _status: entityStateZodSchema,
    _createdAt: isoAndUnixZodSchema,
    firstName: z.string(),
    lastName: z.string(),
    email: emailZodSchema,
    phone: phoneZodSchema,
    scopes: z.array(z.string()),
    preferences: z.object({
        newsletters: z
            .array(z.string() as unknown as z.ZodType<NewsletterId>)
            .optional(),
    }),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isUser = (user: any, exhaustiveCheck = false): user is User => {
    return exhaustiveCheck
        ? userZodSchema.safeParse(user).success
        : user._type === "USER";
};

const toUserReference = (
    user: User | Customer | UserForProvider,
): UserReference =>
    createUserReference({
        _id: user._id,
        firstName: user.firstName,
        lastName: user.lastName,
        image: isUser(user) ? user.image : undefined,
    });

export { isUser, toUserReference, userFactory, userZodSchema };
export type { User };
